// src/components/RidersPortal.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
  Pagination,
  Stack,
  Link,
  InputAdornment,
  Grid,
  Paper,
  alpha,
} from '@mui/material';
import AppDialog, { AppDialogTitle, AppDialogContent, AppDialogActions } from './layout/AppDialog';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListIcon from '@mui/icons-material/FilterList';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import WifiIcon from '@mui/icons-material/Wifi';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import { getOrders, verifyOtp } from '../services/api';
import {
  saveOrders,
  getStoredOrders,
  saveDeliveryAttempt,
  getStoredDeliveryAttempts,
  removeStoredOrder,
} from '../utils/indexedDB';
import { syncOfflineDeliveries } from '../services/syncService';
import { useNavigate } from 'react-router-dom';
import { maskPhoneNumber } from '../utils/phoneUtils';
import { toCacheableUndeliveredOrder, verifyCachedOrderOtp } from '../utils/offlineOtp';
import { california, kepple, stormGrey, redOrange } from '../theme/colors';

const ConnectionStatusBadge = ({ isOnline }) => (
  <Chip
    icon={isOnline ? <WifiIcon /> : <WifiOffIcon />}
    label={isOnline ? 'Online' : 'Offline'}
    size="small"
    sx={{
      fontWeight: 700,
      height: 32,
      px: 0.5,
      bgcolor: isOnline ? alpha(kepple[500], 0.12) : alpha(redOrange[500], 0.12),
      color: isOnline ? kepple[600] : redOrange[500],
      border: `1px solid ${isOnline ? alpha(kepple[500], 0.35) : alpha(redOrange[500], 0.35)}`,
      '& .MuiChip-icon': {
        color: 'inherit',
      },
    }}
  />
);

const StatFilterTile = ({ title, value, icon: Icon, accent, active, onClick }) => (
  <Paper
    elevation={0}
    onClick={onClick}
    sx={{
      p: { xs: 1.5, sm: 2 },
      height: '100%',
      width: '100%',
      cursor: 'pointer',
      borderRadius: 2,
      border: `1px solid ${alpha(accent, active ? 0.45 : 0.14)}`,
      background: active
        ? `linear-gradient(135deg, ${alpha(accent, 0.16)} 0%, #ffffff 55%)`
        : `linear-gradient(135deg, ${alpha(accent, 0.08)} 0%, #ffffff 60%)`,
      position: 'relative',
      overflow: 'hidden',
      boxShadow: active ? `0 6px 18px ${alpha(accent, 0.22)}` : 'none',
      transform: active ? 'translateY(-2px)' : 'none',
      transition: 'box-shadow 0.2s, transform 0.2s, border-color 0.2s',
      '&:hover': {
        boxShadow: `0 6px 18px ${alpha(accent, 0.18)}`,
        transform: 'translateY(-2px)',
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        bgcolor: accent,
      },
    }}
  >
    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: { xs: '0.6875rem', sm: '0.75rem' },
            color: stormGrey[500],
            fontWeight: 600,
            lineHeight: 1.3,
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: '1.5rem', sm: '1.75rem' },
            fontWeight: 700,
            lineHeight: 1.2,
            color: accent,
            mt: 0.25,
          }}
        >
          {value}
        </Typography>
      </Box>
      <Box
        sx={{
          width: { xs: 38, sm: 44 },
          height: { xs: 38, sm: 44 },
          borderRadius: 1.5,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: alpha(accent, 0.14),
          color: accent,
        }}
      >
        <Icon sx={{ fontSize: { xs: 20, sm: 22 } }} />
      </Box>
    </Stack>
  </Paper>
);

const getFirstName = (fullName) => {
  if (!fullName || typeof fullName !== 'string') {
    return '';
  }
  return fullName.split(' ')[0];
};

const OtpModal = ({
  open,
  otp,
  receiverName,
  onReceiverNameChange,
  onOtpChange,
  onOtpSubmit,
  isValidOtp,
  isVerifyingOtp,
  errorMessage,
  selectedOrder,
  onClose,
}) => {
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  return (
    <AppDialog open={open} onClose={onClose} maxWidth="xs">
      <AppDialogTitle onClose={onClose}>Enter Delivery Details</AppDialogTitle>
      <AppDialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {selectedOrder?.order_no
            ? `Order #${selectedOrder.order_no}`
            : `Waybill #${selectedOrder?.waybill_no}`}
        </Typography>

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        <TextField
          fullWidth
          label="Receiver's Full Name"
          value={receiverName}
          onChange={onReceiverNameChange}
          required
          margin="normal"
        />

        <TextField
          inputRef={inputRef}
          fullWidth
          label="6-digit OTP"
          value={otp}
          onChange={onOtpChange}
          type="tel"
          inputMode="numeric"
          autoComplete="one-time-code"
          margin="normal"
          error={Boolean(otp && !isValidOtp(otp))}
          helperText={otp && !isValidOtp(otp) ? 'Please enter exactly 6 digits' : ''}
          inputProps={{ maxLength: 6 }}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === 'Go') && !isValidOtp(otp)) {
              e.preventDefault();
            }
          }}
        />
      </AppDialogContent>
      <AppDialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', pb: 3 }}>
        <Button
          fullWidth
          variant="contained"
          onClick={onOtpSubmit}
          disabled={!isValidOtp(otp) || !receiverName?.trim() || isVerifyingOtp}
        >
          {isVerifyingOtp ? 'Verifying...' : 'Submit Delivery'}
        </Button>
        <Button fullWidth variant="outlined" color="inherit" onClick={onClose}>
          Cancel
        </Button>
      </AppDialogActions>
    </AppDialog>
  );
};

const RidersPortal = () => {
  const [orders, setOrders] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState(null);
  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [otp, setOtp] = useState('');
  const [receiverName, setReceiverName] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const recordsPerPage = 25;
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingSyncCount, setPendingSyncCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [usingCachedOrders, setUsingCachedOrders] = useState(false);
  const navigate = useNavigate();
  const hasCheckedEmptyState = useRef(false);

  const refreshPendingSyncCount = async () => {
    try {
      const attempts = await getStoredDeliveryAttempts();
      setPendingSyncCount(attempts.length);
    } catch (error) {
      setPendingSyncCount(0);
    }
  };

  const loadCachedOrders = async () => {
    const cachedOrders = await getStoredOrders();
    const attempts = await getStoredDeliveryAttempts();

    const undelivered = (cachedOrders || []).filter(
      (order) => order.status_id === 2 || order.status_id === 4
    );

    const pendingDelivered = (attempts || [])
      .map((attempt) => attempt.orderSnapshot)
      .filter(Boolean);

    const relevantOrders = [...undelivered, ...pendingDelivered].sort((a, b) => {
      if (a.status_id !== b.status_id) {
        return a.status_id - b.status_id;
      }
      return new Date(b.createdAt) - new Date(a.createdAt);
    });

    setOrders(relevantOrders);
    setUsingCachedOrders(true);
    return relevantOrders;
  };

  const markOrderDeliveredLocally = async (order, receiverNameValue) => {
    const deliveredAt = new Date().toISOString();
    const deliveredOrder = {
      ...order,
      status_id: 3,
      delivered_at: deliveredAt,
      received_by: receiverNameValue,
      pending_offline_sync: true,
      otp: undefined,
      otp_expires_at: undefined,
    };

    setOrders((prevOrders) =>
      prevOrders.map((item) => (item.id === order.id ? deliveredOrder : item))
    );

    // Undelivered cache should only keep orders still awaiting delivery
    await removeStoredOrder(order.id);
    return deliveredOrder;
  };

  const queueOfflineDelivery = async (order, otpValue, receiverNameValue) => {
    const deliveredOrder = await markOrderDeliveredLocally(order, receiverNameValue);

    await saveDeliveryAttempt({
      orderId: order.id,
      otp: otpValue,
      receiverName: receiverNameValue,
      waybillNo: order.waybill_no,
      createdAt: new Date().toISOString(),
      orderSnapshot: deliveredOrder,
    });
    await refreshPendingSyncCount();

    const deliveryTime = new Date().toLocaleString();
    setSuccessMessage(
      `Delivery saved offline for order #${order.waybill_no} at ${deliveryTime}. It will sync when you're back online.`
    );
    setIsSuccessModalOpen(true);
    setTimeout(() => setIsSuccessModalOpen(false), 3500);
  };

  const syncPendingDeliveries = async () => {
    if (!navigator.onLine || isSyncing) {
      return;
    }

    setIsSyncing(true);
    try {
      const result = await syncOfflineDeliveries();
      await refreshPendingSyncCount();

      if (result.total > 0) {
        if (result.successCount > 0) {
          setSuccessMessage(
            `Synced ${result.successCount} offline deliver${result.successCount === 1 ? 'y' : 'ies'}.`
          );
          setIsSuccessModalOpen(true);
          setTimeout(() => setIsSuccessModalOpen(false), 2500);
        }
        await fetchOrders();
      }
    } catch (error) {
      // keep queued attempts for next reconnect
    } finally {
      setIsSyncing(false);
    }
  };

  const fetchOrders = async () => {
    setIsLoading(true);
    try {
      if (!navigator.onLine) {
        await loadCachedOrders();
        setErrorMessage('');
        return;
      }

      const response = await getOrders();
      const relevantOrders = response.data.filter(
        (order) => order.status_id === 2 || order.status_id === 3 || order.status_id === 4
      );

      const sortedOrders = relevantOrders.sort((a, b) => {
        if (a.status_id !== b.status_id) {
          return a.status_id - b.status_id;
        }
        return new Date(b.createdAt) - new Date(a.createdAt);
      });

      // Cache only undelivered orders, including their OTP hashes for offline verification
      const cacheableOrders = sortedOrders
        .map(toCacheableUndeliveredOrder)
        .filter(Boolean);
      await saveOrders(cacheableOrders);

      // Keep any pending offline deliveries visible until sync completes
      const attempts = await getStoredDeliveryAttempts();
      const pendingDelivered = (attempts || [])
        .map((attempt) => attempt.orderSnapshot)
        .filter(Boolean);
      const pendingIds = new Set(pendingDelivered.map((order) => order.id));
      const mergedOrders = [
        ...sortedOrders.filter((order) => !pendingIds.has(order.id)),
        ...pendingDelivered,
      ].sort((a, b) => {
        if (a.status_id !== b.status_id) {
          return a.status_id - b.status_id;
        }
        return new Date(b.createdAt) - new Date(a.createdAt);
      });

      setOrders(mergedOrders);
      setUsingCachedOrders(false);
      setErrorMessage('');
    } catch (error) {
      try {
        const cached = await loadCachedOrders();
        if (cached.length > 0) {
          setErrorMessage('Showing cached orders. Some updates may be unavailable offline.');
        } else {
          setErrorMessage('Error fetching orders. Go online once to cache your deliveries.');
        }
      } catch (cacheError) {
        setErrorMessage('Error fetching orders');
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setOrders([]);
    fetchOrders();
    refreshPendingSyncCount();
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      syncPendingDeliveries();
    };
    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    if (navigator.onLine) {
      syncPendingDeliveries();
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    let lastFetchTime = Date.now();
    const MIN_REFRESH_INTERVAL = 5000;

    const handleVisibilityChange = () => {
      if (!document.hidden && navigator.onLine) {
        const now = Date.now();
        if (now - lastFetchTime > MIN_REFRESH_INTERVAL) {
          lastFetchTime = now;
          syncPendingDeliveries().then(() => fetchOrders());
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (hasCheckedEmptyState.current) return;

    const timer = setTimeout(() => {
      if (orders.length === 0 && !isLoading && navigator.onLine) {
        fetchOrders();
      }
      hasCheckedEmptyState.current = true;
    }, 2000);

    return () => clearTimeout(timer);
  }, [orders.length, isLoading]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userRole = localStorage.getItem('userRole');

    if (!token || !userRole || userRole !== 'rider') {
      navigate('/login');
    }
  }, [navigate]);

  const handleSearch = async (event) => {
    const searchValue = event.target.value;
    setSearchQuery(searchValue);
    setCurrentPage(1);

    if (searchValue.trim() === '' && navigator.onLine) {
      await fetchOrders();
    }
  };

  const handleRefresh = async () => {
    if (navigator.onLine) {
      await syncPendingDeliveries();
    }
    await fetchOrders();
  };

  const filteredOrders = orders.filter((order) => {
    if (statusFilter !== null && order.status_id !== statusFilter) {
      return false;
    }

    const searchLower = searchQuery.toLowerCase();
    return (
      order.waybill_no.toLowerCase().includes(searchLower) ||
      (order.order_no && order.order_no.toLowerCase().includes(searchLower)) ||
      order.customer_name.toLowerCase().includes(searchLower) ||
      getFirstName(order.customer_name).toLowerCase().includes(searchLower) ||
      (order.customer_phone && order.customer_phone.toLowerCase().includes(searchLower)) ||
      (order.customer_phone &&
        maskPhoneNumber(order.customer_phone).toLowerCase().includes(searchLower)) ||
      order.customer_address.toLowerCase().includes(searchLower)
    );
  });

  const handleStatusFilter = (statusId) => {
    if (statusFilter === statusId) {
      setStatusFilter(null);
    } else {
      setStatusFilter(statusId);
    }
    setCurrentPage(1);
  };

  const handleDeliverClick = (order) => {
    setSelectedOrderId(order.id);
    setSelectedOrder(order);
    setIsOtpModalOpen(true);
  };

  const isValidOtp = (value) => /^\d{6}$/.test(value);

  const handleReceiverNameChange = (e) => {
    setReceiverName(e.target.value);
  };

  const handleOtpChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d+$/.test(value)) {
      setOtp(value.slice(0, 6));
    }
  };

  const handleOtpSubmit = async () => {
    if (!selectedOrderId || !otp || !receiverName?.trim()) return;

    setIsOtpModalOpen(false);
    const otpValue = otp;
    const receiverNameValue = receiverName.trim();
    const orderId = selectedOrderId;
    const orderForDelivery = selectedOrder;
    const waybillNo = selectedOrder?.waybill_no;
    setOtp('');
    setReceiverName('');

    setVerificationMessage(`Verifying OTP for order #${waybillNo}...`);
    setIsVerifyingOtp(true);
    setErrorMessage('');

    const reopenWithError = (message) => {
      setIsVerifyingOtp(false);
      setVerificationMessage('');
      setErrorMessage(message);
      setSelectedOrderId(orderId);
      setSelectedOrder(orderForDelivery);
      setReceiverName(receiverNameValue);
      setOtp(otpValue);
      setIsOtpModalOpen(true);
    };

    // Offline delivery: verify OTP against cached hash first
    if (!navigator.onLine) {
      try {
        const cachedOrders = await getStoredOrders();
        const cachedOrder =
          cachedOrders.find((order) => order.id === orderId)
          || (orderForDelivery?.otp ? orderForDelivery : null);

        const otpCheck = await verifyCachedOrderOtp(cachedOrder, otpValue);
        if (!otpCheck.valid) {
          reopenWithError(otpCheck.message);
          return;
        }

        await queueOfflineDelivery(cachedOrder || orderForDelivery, otpValue, receiverNameValue);
      } catch (error) {
        reopenWithError('Could not save offline delivery. Please try again.');
      } finally {
        setIsVerifyingOtp(false);
        setVerificationMessage('');
      }
      return;
    }

    try {
      const response = await verifyOtp(orderId, otpValue, receiverNameValue);
      setIsVerifyingOtp(false);
      setVerificationMessage('');

      if (response.data && response.data.message === 'Order status updated to delivered') {
        setOrders((prevOrders) =>
          prevOrders.map((order) =>
            order.id === orderId
              ? {
                  ...order,
                  status_id: 3,
                  delivered_at: new Date().toISOString(),
                  received_by: receiverNameValue,
                  pending_offline_sync: false,
                  otp: undefined,
                  otp_expires_at: undefined,
                }
              : order
          )
        );
        await removeStoredOrder(orderId);
        const deliveryTime = new Date().toLocaleString();
        setSuccessMessage(
          `Delivery confirmed for order #${waybillNo} at ${deliveryTime}`
        );
        setIsSuccessModalOpen(true);
        setTimeout(() => {
          setIsSuccessModalOpen(false);
          fetchOrders();
        }, 3000);
      } else {
        reopenWithError(response.data.message || 'OTP verification failed. Please try again.');
      }
    } catch (error) {
      setIsVerifyingOtp(false);
      setVerificationMessage('');

      // Network failure while "online": verify cached OTP, then queue for later sync
      if (!error.response) {
        try {
          const cachedOrders = await getStoredOrders();
          const cachedOrder =
            cachedOrders.find((order) => order.id === orderId)
            || (orderForDelivery?.otp ? orderForDelivery : null);

          const otpCheck = await verifyCachedOrderOtp(cachedOrder, otpValue);
          if (!otpCheck.valid) {
            reopenWithError(otpCheck.message);
            return;
          }

          await queueOfflineDelivery(cachedOrder || orderForDelivery, otpValue, receiverNameValue);
          return;
        } catch (queueError) {
          reopenWithError('Could not save offline delivery. Please try again.');
          return;
        }
      }

      let userFriendlyMessage = 'Error verifying OTP. Please try again.';
      if (error.response?.data?.message) {
        userFriendlyMessage = error.response.data.message;
      } else if (error.message) {
        userFriendlyMessage = error.message;
      }

      reopenWithError(userFriendlyMessage);
    }
  };

  const closeAllModals = () => {
    setIsOtpModalOpen(false);
    setIsSuccessModalOpen(false);
    setOtp('');
    setReceiverName('');
    setErrorMessage('');
  };

  const indexOfLastRecord = currentPage * recordsPerPage;
  const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
  const currentRecords = filteredOrders.slice(indexOfFirstRecord, indexOfLastRecord);
  const totalPages = Math.ceil(filteredOrders.length / recordsPerPage);

  const pendingCount = orders.filter((order) => order.status_id === 2).length;
  const deliveredCount = orders.filter((order) => order.status_id === 3).length;

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{ mb: 1.5 }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: stormGrey[900] }}>
          Rider deliveries
        </Typography>
        <ConnectionStatusBadge isOnline={isOnline} />
      </Stack>

      {!isOnline && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          You are offline. You can still deliver orders — they will sync when you reconnect.
        </Alert>
      )}

      {isOnline && pendingSyncCount > 0 && (
        <Alert
          severity="info"
          sx={{ mb: 1.5 }}
          action={
            <Button color="inherit" size="small" onClick={syncPendingDeliveries} disabled={isSyncing}>
              {isSyncing ? 'Syncing...' : 'Sync now'}
            </Button>
          }
        >
          {pendingSyncCount} offline deliver{pendingSyncCount === 1 ? 'y' : 'ies'} waiting to sync.
        </Alert>
      )}

      {usingCachedOrders && isOnline && (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Showing cached orders. Tap Refresh to load the latest list.
        </Alert>
      )}

      <Stack spacing={1.5} sx={{ mb: 1.5 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1,
          }}
        >
          <TextField
            fullWidth
            placeholder="Search orders..."
            value={searchQuery}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FilterListIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <Button
              variant="contained"
              onClick={handleRefresh}
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
              sx={{ minWidth: { sm: 140 }, flex: { xs: 1, sm: 'none' }, flexShrink: 0 }}
            >
              Refresh
            </Button>
          </Stack>
        </Box>

        <Grid container spacing={1.5}>
          <Grid size={{ xs: 6 }}>
            <StatFilterTile
              title="Pending Delivery"
              value={pendingCount}
              icon={LocalShippingIcon}
              accent={california[500]}
              active={statusFilter === 2}
              onClick={() => handleStatusFilter(2)}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <StatFilterTile
              title="Total Delivered"
              value={deliveredCount}
              icon={CheckCircleIcon}
              accent={kepple[500]}
              active={statusFilter === 3}
              onClick={() => handleStatusFilter(3)}
            />
          </Grid>
        </Grid>

        {statusFilter !== null && (
          <Button size="small" onClick={() => handleStatusFilter(null)} sx={{ alignSelf: 'flex-start', px: 0 }}>
            Show all orders
          </Button>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          Tap a tile to filter orders
        </Typography>
      </Stack>

      {isLoading && (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={28} />
        </Box>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        Showing {currentRecords.length} of {filteredOrders.length} orders
      </Typography>

      <Stack spacing={1}>
        {currentRecords.map((order) => (
          <Card
            key={order.id}
            elevation={1}
            sx={{
              borderLeft: 3,
              borderColor: order.status_id === 2 ? 'warning.main' : 'success.main',
            }}
          >
            <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  mb: 0.75,
                  gap: 1,
                }}
              >
                <Typography variant="subtitle2" component="span" fontWeight="bold">
                  #{order.waybill_no}
                </Typography>
                <Chip
                  label={
                    order.status_id === 2
                      ? 'Pending Delivery'
                      : order.pending_offline_sync
                        ? 'Delivered (pending sync)'
                        : 'Delivered'
                  }
                  color={order.status_id === 2 ? 'warning' : order.pending_offline_sync ? 'info' : 'success'}
                  size="small"
                />
              </Box>

              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                {order.order_no ? `Order #${order.order_no}` : `Waybill #${order.waybill_no}`}
                {' · '}
                {new Date(order.createdAt).toLocaleString()}
              </Typography>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                  gap: 0.75,
                  mb: order.status_id === 2 ? 1 : 0,
                }}
              >
                <Typography variant="body2">
                  <strong>Customer:</strong>{' '}
                  {order.status_id === 3 ? getFirstName(order.customer_name) : order.customer_name}
                </Typography>
                <Typography variant="body2">
                  <strong>Phone:</strong>{' '}
                  <Link href={`tel:${order.customer_phone}`} underline="hover">
                    {order.status_id === 3
                      ? maskPhoneNumber(order.customer_phone)
                      : order.customer_phone}
                  </Link>
                </Typography>
                <Typography variant="body2" sx={{ gridColumn: { sm: '1 / -1' } }}>
                  <strong>Address:</strong> {order.customer_address}
                </Typography>
                {order.status_id === 3 && order.received_by && (
                  <Typography variant="body2" color="success.main">
                    <strong>Received by:</strong> {order.received_by}
                  </Typography>
                )}
                {order.status_id === 3 && order.delivered_at && (
                  <Typography variant="body2" color="success.main">
                    <strong>Delivered:</strong> {new Date(order.delivered_at).toLocaleString()}
                  </Typography>
                )}
              </Box>

              {order.status_id === 2 && (
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => handleDeliverClick(order)}
                >
                  Deliver Order
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
      </Stack>

      {filteredOrders.length > recordsPerPage && (
        <Box display="flex" justifyContent="center" mt={1.5}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(event, value) => setCurrentPage(value)}
            color="primary"
            size="small"
            siblingCount={0}
          />
        </Box>
      )}

      {filteredOrders.length === 0 && !isLoading && (
        <Box textAlign="center" py={4}>
          <Typography color="text.secondary">No orders found</Typography>
        </Box>
      )}

      <OtpModal
        open={isOtpModalOpen}
        otp={otp}
        receiverName={receiverName}
        onReceiverNameChange={handleReceiverNameChange}
        onOtpChange={handleOtpChange}
        onOtpSubmit={handleOtpSubmit}
        isValidOtp={isValidOtp}
        isVerifyingOtp={isVerifyingOtp}
        errorMessage={errorMessage}
        selectedOrder={selectedOrder}
        onClose={closeAllModals}
      />

      <AppDialog open={isSuccessModalOpen} maxWidth="xs" disableEscapeKeyDown>
        <AppDialogContent dividers={false} sx={{ textAlign: 'center', py: 4 }}>
          <CheckCircleIcon color="success" sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Delivery Successful!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {successMessage}
          </Typography>
        </AppDialogContent>
      </AppDialog>

      <AppDialog open={isVerifyingOtp} maxWidth="xs" disableEscapeKeyDown>
        <AppDialogContent dividers={false} sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress sx={{ mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Verifying Delivery
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {verificationMessage}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Please wait a moment...
          </Typography>
        </AppDialogContent>
      </AppDialog>
    </Box>
  );
};

export default RidersPortal;
